import React, { useEffect } from 'react';
import { InjectedRouter } from 'react-router';
import { groupBy } from 'lodash';
import { isAfter, subMinutes } from 'date-fns';
// @ts-ignore
import Config from 'Config';
import Button from 'components/Button';
import EventTracking from 'lib/EventTracking';
import TextWithIcon from 'components/TextWithIcon';

import CoachingHomeWelcomeHeader from 'coaching/components/CoachingHomeView/CoachingHomeWelcomeHeader';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingView from 'coaching/components/CoachingView';
import CoachingHomeParticipantSectionHeader from 'coaching/components/CoachingHomeParticipantSectionHeader';
import CoachingSessionsBySubjectContainer from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';

import {
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard,
} from 'coaching/queries/__generated__/GetCoachingHomeDashboard';
import CurrentUser from 'auth/CurrentUser';

export type Props = {
  coachingHomeDashboard: GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard;
  router: InjectedRouter;
};

export const coachingHomeEventPrefix = 'coaching.coaching_home_dashboard';

export default function CoachingHomeView({ coachingHomeDashboard, router }: Props) {
  useEffect(() => {
    if (coachingHomeDashboard.participants.length === 0) {
      router.push('/');
      return;
    }

    EventTracking.trackKey(`${coachingHomeEventPrefix}.view`);
  }, []);

  if (coachingHomeDashboard.participants.length === 0) {
    return null;
  }

  const user = CurrentUser.getUser();

  return (
    <CoachingView helpButtonEventKey={`${coachingHomeEventPrefix}.get_help.click`}>
      <CoachingHomeWelcomeHeader
        firstName={user.getFirstName()}
        hasUpcomingSessions={coachingHomeDashboard.participants.some(
          p => p.upcomingCoachingSessionInstances.length > 0
        )}
        data-test="welcome-header"
      />
      {coachingHomeDashboard.participants.map(participant => (
        <div key={participant.uuid} data-test={`participant-section-${participant.uuid}`}>
          <CoachingHomeParticipantSectionHeader participant={participant} />
          {getSessionsCards(participant, user)}
        </div>
      ))}
      {isParent(coachingHomeDashboard) && (
        <Button
          eventKey={`${coachingHomeEventPrefix}.add_a_student.click`}
          data-test="add-a-student-button"
          size="small"
          to={CoachingConstants.COACHING_PURCHASE_INTRO_ROUTE}>
          <TextWithIcon icon="plus-circle">Add a student</TextWithIcon>
        </Button>
      )}
    </CoachingView>
  );
}

/**
 * If _any_ of the student uuids for the participants do NOT equal the passed in UUID, then we're
 * looking at someone who's a parent.
 * This is a bit convoluted and would be better served with a "isParent" code
 */
function isParent(
  coachingHomeDashboard: GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard
) {
  return (
    coachingHomeDashboard.participants.find(participant => {
      return participant.participantMetadata.studentUuid !== coachingHomeDashboard.uuid;
    }) !== undefined
  );
}

function getUpcomingInstancesGroupedBySubjectName(
  participant: GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants
) {
  const upcomingInstances = participant.upcomingCoachingSessionInstances.filter(i =>
    // we should count an instance as upcoming even if it's currently in progress,
    // so check that the startDateTime of the instance is after the current time
    // minus the duration of the session
    isAfter(i.startDateTime, subMinutes(new Date(), parseInt(i.duration)))
  );
  return groupBy(upcomingInstances, instance => instance.subject.name);
}

function getRecurringSessionSchedulesGroupedBySubjectName(
  participant: GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants
) {
  const recurringSchedules = participant.coachingSessionSchedules.filter(s => s.isRecurring);
  return groupBy(recurringSchedules, schedule => schedule.subject.name);
}

/**
 * Gathers all the individual sessions this user has.
 * If a participant has no sessions, return a session info container with an empty coaching session.
 * We also want to hide the free trial sessions after a user has completed a free trial session and has scheduled more
 * sessions.
 */
function getSessionsCards(
  participant: GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants,
  user: any
) {
  const hasOptOutExperiment = Config.Environment.COACHING_OPT_OUT_ENABLED === 'true';
  const recurringSessionSchedules = getRecurringSessionSchedulesGroupedBySubjectName(participant);
  const upcomingInstancesBySubject = getUpcomingInstancesGroupedBySubjectName(participant);
  const subjectsParticipantHasStudied = participant.sessionTools.map(s => s.subject.name);

  if (subjectsParticipantHasStudied.length === 0) {
    return (
      <CoachingSessionsBySubjectContainer
        recurringSessionSchedules={[]}
        upcomingInstances={[]}
        previousCoaches={participant.previousCoaches}
        participantMetadata={participant.participantMetadata}
        hasOptOutExperiment={hasOptOutExperiment}
        hasStudentInvitation={!!participant.studentInvitation}
        userUuid={user.getUuid()}
      />
    );
  }

  return subjectsParticipantHasStudied.map(subjectName => {
    const sessionSchedules = recurringSessionSchedules[subjectName];
    const upcomingInstances = upcomingInstancesBySubject[subjectName];
    const sessionTools = participant.sessionTools.find(tools => tools.subject.name === subjectName);
    return (
      <CoachingSessionsBySubjectContainer
        key={subjectName}
        recurringSessionSchedules={sessionSchedules}
        upcomingInstances={upcomingInstances}
        previousCoaches={participant.previousCoaches}
        participantMetadata={participant.participantMetadata}
        subjectName={subjectName}
        sessionTools={sessionTools}
        hasOptOutExperiment={hasOptOutExperiment}
        hasStudentInvitation={!!participant.studentInvitation}
        userUuid={user.getUuid()}
      />
    );
  });
}
